import { slideDesignSchema, componentFrameSchema, componentAppearanceSchema } from "./slide-design";
import { z } from "zod";
import { generationPreferencesSchema } from "./model-preferences";

export const CANONICAL_SCHEMA_VERSION = "1.0.0" as const;

const schemaVersionSchema = z.literal(CANONICAL_SCHEMA_VERSION);
const identifierSchema = z.string().trim().min(1).max(200);
const timestampSchema = z.string().datetime({ offset: true });
const nonEmptyTextSchema = z.string().trim().min(1);

export const artifactHashSchema = z
  .string()
  .regex(/^sha256:[a-f0-9]{64}$/, "Expected a lowercase sha256 content hash");

export const artifactKindSchema = z.enum([
  "project_spec",
  "workspace_snapshot",
  "source_map",
  "presentation_brief",
  "storyline",
  "deck_spec",
  "theme_spec",
  "visual_prompt_plan",
  "run_state",
  "evaluation_report",
  "delivery_manifest",
  "source",
  "image_asset",
  "chart_data",
  "table_data",
]);

export const artifactRefSchema = z
  .object({
    schemaVersion: schemaVersionSchema,
    artifactId: identifierSchema,
    kind: artifactKindSchema,
    revision: z.number().int().positive(),
    hash: artifactHashSchema,
    createdAt: timestampSchema,
    mediaType: z.string().trim().min(1).optional(),
  })
  .strict();

export const projectSpecSchema = z
  .object({
    schemaVersion: schemaVersionSchema,
    projectId: identifierSchema,
    revisionId: identifierSchema,
    name: nonEmptyTextSchema,
    surface: z.enum(["web", "windows"]),
    generationPreferences: generationPreferencesSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict();

const slideCountSchema = z
  .object({
    min: z.number().int().positive(),
    max: z.number().int().positive(),
  })
  .strict()
  .refine(({ min, max }) => min <= max, {
    message: "Minimum slide count cannot exceed maximum slide count",
    path: ["max"],
  });

const sourceScopeSchema = z
  .object({
    included: z.array(nonEmptyTextSchema),
    excluded: z.array(nonEmptyTextSchema),
    blocked: z.array(nonEmptyTextSchema),
  })
  .strict();

export const deliverableSchema = z.enum([
  "pptx",
  "pdf",
  "slide_previews",
  "speaker_notes",
]);

export const presentationBriefSchema = z
  .object({
    schemaVersion: schemaVersionSchema,
    artifactId: identifierSchema,
    projectId: identifierSchema,
    revisionId: identifierSchema,
    objective: nonEmptyTextSchema,
    audience: nonEmptyTextSchema,
    setting: nonEmptyTextSchema,
    desiredAudienceAction: nonEmptyTextSchema,
    speakingTimeMinutes: z.number().positive(),
    slideCount: slideCountSchema,
    sourceScope: sourceScopeSchema,
    constraints: z.array(nonEmptyTextSchema),
    deliverables: z.array(deliverableSchema).min(1),
    createdAt: timestampSchema,
  })
  .strict();

const storylineSectionSchema = z
  .object({
    sectionId: identifierSchema,
    title: nonEmptyTextSchema,
    order: z.number().int().positive(),
    slideIds: z.array(identifierSchema).min(1),
  })
  .strict();

export const visualRoleSchema = z.enum([
  "text",
  "image",
  "chart",
  "table",
  "diagram",
  "comparison",
  "process",
  "demo",
]);

const storylineSlideSchema = z
  .object({
    slideId: identifierSchema,
    order: z.number().int().positive(),
    purpose: nonEmptyTextSchema,
    evidenceRefs: z.array(artifactRefSchema),
    visualRole: visualRoleSchema,
    design: slideDesignSchema.optional(),
    estimatedSeconds: z.number().nonnegative(),
    placeholders: z.array(nonEmptyTextSchema),
    locked: z.boolean(),
  })
  .strict();

export const storylineSchema = z
  .object({
    schemaVersion: schemaVersionSchema,
    artifactId: identifierSchema,
    projectId: identifierSchema,
    revisionId: identifierSchema,
    thesis: nonEmptyTextSchema,
    sections: z.array(storylineSectionSchema).min(1),
    slides: z.array(storylineSlideSchema).min(1),
    createdAt: timestampSchema,
  })
  .strict();

const componentBase = {
  frame: componentFrameSchema.optional(),
  appearance: componentAppearanceSchema.optional(),
  componentId: identifierSchema,
  locked: z.boolean(),
};

export const slideComponentSchema = z.discriminatedUnion("kind", [
  z
    .object({
      ...componentBase,
      kind: z.literal("text"),
      role: z.enum(["title", "body", "label", "citation", "page_number"]),
      text: nonEmptyTextSchema,
    })
    .strict(),
  z
    .object({
      ...componentBase,
      kind: z.literal("image"),
      assetRef: artifactRefSchema,
      altText: nonEmptyTextSchema,
    })
    .strict(),
  z
    .object({
      ...componentBase,
      kind: z.literal("chart"),
      dataRef: artifactRefSchema,
      chartType: z.enum(["bar", "column", "line", "area", "pie", "scatter", "doughnut"]),
    })
    .strict(),
  z
    .object({
      ...componentBase,
      kind: z.literal("table"),
      dataRef: artifactRefSchema,
    })
    .strict(),
  z
    .object({
      ...componentBase,
      kind: z.literal("shape"),
      shapeType: z.enum(["rectangle", "line", "arrow", "ellipse"]),
    })
    .strict(),
]);

export const slideLayoutIdSchema = z.enum([
  "title_body",
  "two_column",
  "hero",
  "data_focus",
  "cover", "timeline", "line_chart", "organization", "comparison", "closing", "statement", "image_split",
  "matrix", "columns", "table", "flow",
]);

const deckSlideSchema = z
  .object({
    slideId: identifierSchema,
    order: z.number().int().positive(),
    purpose: nonEmptyTextSchema,
    layoutId: slideLayoutIdSchema.optional(),
    components: z.array(slideComponentSchema).min(1),
    evidenceRefs: z.array(artifactRefSchema),
    speakerNotes: z.string(),
    locked: z.boolean(),
  })
  .strict();

export const deckSpecSchema = z
  .object({
    schemaVersion: schemaVersionSchema,
    artifactId: identifierSchema,
    projectId: identifierSchema,
    revisionId: identifierSchema,
    title: nonEmptyTextSchema,
    aspectRatio: z.literal("16:9"),
    themeRef: artifactRefSchema,
    slides: z.array(deckSlideSchema).min(1),
    createdAt: timestampSchema,
  })
  .strict();

const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hexadecimal color");

export const themeSpecSchema = z
  .object({
    schemaVersion: schemaVersionSchema,
    artifactId: identifierSchema,
    projectId: identifierSchema,
    revisionId: identifierSchema,
    typography: z
      .object({
        headingFont: nonEmptyTextSchema,
        bodyFont: nonEmptyTextSchema,
        monoFont: nonEmptyTextSchema.optional(),
      })
      .strict(),
    palette: z
      .object({
        background: hexColorSchema,
        foreground: hexColorSchema,
        accent: hexColorSchema,
        muted: hexColorSchema,
      })
      .strict(),
    grid: z
      .object({
        columns: z.number().int().min(1).max(24),
        margin: z.number().nonnegative(),
        gutter: z.number().nonnegative(),
      })
      .strict(),
    spacing: z.object({ unit: z.number().positive() }).strict(),
    imagery: z
      .object({
        style: nonEmptyTextSchema,
        treatment: z.enum(["natural", "duotone", "monochrome", "cutout"]),
      })
      .strict(),
    compositionRules: z.array(nonEmptyTextSchema).min(1),
    createdAt: timestampSchema,
  })
  .strict();

export type ArtifactHash = z.infer<typeof artifactHashSchema>;
export type ArtifactKind = z.infer<typeof artifactKindSchema>;
export type ArtifactRef = z.infer<typeof artifactRefSchema>;
export type ProjectSpec = z.infer<typeof projectSpecSchema>;
export type PresentationBrief = z.infer<typeof presentationBriefSchema>;
export type Storyline = z.infer<typeof storylineSchema>;
export type SlideLayoutId = z.infer<typeof slideLayoutIdSchema>;
export type SlideComponent = z.infer<typeof slideComponentSchema>;
export type DeckSpec = z.infer<typeof deckSpecSchema>;
export type ThemeSpec = z.infer<typeof themeSpecSchema>;
